import { useEffect, useSyncExternalStore } from "react";
import { CONTEXTS } from "../data/tracks";

// One audio element shared by every player view (Spotify, Control Center, the menu bar), so they
// always agree on what's playing. It plays through a context, an album or playlist (data/tracks.js),
// and starts where Sylvester's Spotify was: ICEMAN's first track, paused.
let audio = null;
let state = {
  context: "iceman",
  index: 0,
  playing: false,
  time: 0,
  duration: 0,
  volume: 0.5,
  shuffle: false,
  repeat: "off", // "off", "context" (the whole album or playlist), or "track"
  upNext: 1, // the track that plays next (decided ahead, so the queue can show it)
};
const listeners = new Set();

const emit = (patch) => {
  state = { ...state, ...patch };
  listeners.forEach((listener) => listener());
};

const tracksOf = (context) => CONTEXTS[context].tracks;

const getAudio = () => {
  if (audio) return audio;
  audio = new Audio(tracksOf(state.context)[state.index].src);
  audio.preload = "metadata";
  audio.volume = state.volume;
  audio.addEventListener("timeupdate", () => emit({ time: audio.currentTime }));
  audio.addEventListener("loadedmetadata", () => emit({ duration: audio.duration }));
  audio.addEventListener("play", () => emit({ playing: true }));
  audio.addEventListener("pause", () => emit({ playing: false }));
  audio.addEventListener("ended", ended);
  return audio;
};

// The track after `index`: the next one, or any other one when shuffling.
const pickNext = (context, index, shuffle) => {
  const count = tracksOf(context).length;
  if (shuffle && count > 1) {
    const other = Math.floor(Math.random() * (count - 1));
    return other >= index ? other + 1 : other;
  }
  return (index + 1) % count;
};

const load = (context, index, autoplay) => {
  const a = getAudio();
  a.src = tracksOf(context)[index].src;
  emit({ context, index, time: 0, duration: 0, upNext: pickNext(context, index, state.shuffle) });
  if (autoplay) a.play().catch(() => {});
};

const previousIndex = () => {
  const count = tracksOf(state.context).length;
  return (state.index - 1 + count) % count;
};

// As Spotify does at the end of a song: play it again, go on, or stop after the last one.
function ended() {
  if (state.repeat === "track") {
    audio.currentTime = 0;
    audio.play().catch(() => {});
    return;
  }
  load(state.context, state.upNext, !endsHere());
}

// Whether playback stops after this track: the last one, with neither shuffle nor repeat on.
const endsHere = () => state.index === tracksOf(state.context).length - 1 && !state.shuffle && state.repeat === "off";

const REPEAT_NEXT = { off: "context", context: "track", track: "off" };

export const player = {
  // Plays a context's track from the start (or the context's first track).
  play(context, index = 0) {
    load(context, index, true);
  },
  toggle() {
    const a = getAudio();
    if (a.paused) a.play().catch(() => {});
    else a.pause();
  },
  next() {
    load(state.context, state.upNext, !getAudio().paused);
  },
  // A few seconds in, "back" restarts the song; otherwise it goes back a track.
  previous() {
    const a = getAudio();
    if (a.currentTime > 3) {
      a.currentTime = 0;
      emit({ time: 0 });
      return;
    }
    load(state.context, previousIndex(), !a.paused);
  },
  seek(time) {
    getAudio().currentTime = time;
    emit({ time });
  },
  setVolume(volume) {
    getAudio().volume = volume;
    emit({ volume });
  },
  toggleShuffle() {
    emit({ shuffle: !state.shuffle, upNext: pickNext(state.context, state.index, !state.shuffle) });
  },
  cycleRepeat() {
    emit({ repeat: REPEAT_NEXT[state.repeat] });
  },
};

const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

// What's playing: the player's state, the `track` (with its artists as one line, `artist`), the
// `playlist` it plays from, and the track after it (`next`, unless playback stops here).
const usePlayer = () => {
  // Create the audio element as soon as a player is on screen, so its duration is known.
  useEffect(() => {
    getAudio();
  }, []);
  const snapshot = useSyncExternalStore(subscribe, () => state);
  const playlist = CONTEXTS[snapshot.context];
  const track = playlist.tracks[snapshot.index];
  const next = endsHere() ? null : playlist.tracks[snapshot.upNext];
  return { ...snapshot, playlist, next, track: { ...track, artist: track.artists.join(", ") } };
};

export default usePlayer;

// 00:01, as Now Playing shows it.
export const formatPlayback = (seconds) => {
  if (!Number.isFinite(seconds)) return "--:--";
  const s = Math.max(0, Math.floor(seconds));
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
};
